/**
 * /_design — component gallery for QA screenshots (FOUNDATION — v3).
 * Every design-system primitive with its variants + motion states, plus
 * theme + announcer volume switchers. Not linked from user nav; not a user
 * surface.
 */
import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { copy, resolveCopy } from "@humble/shared";
import {
  AmbientBackground,
  announce,
  BerserkerBadge,
  Button,
  Card,
  GrainOverlay,
  HijackedBadge,
  IconBrokenHeart,
  IconShield,
  IconSkull,
  KillstreakBadge,
  Mascot,
  pageVariants,
  QuotaChip,
  Reveal,
  shakeScreen,
  Sheet,
  Skeleton,
  SwipeCard,
  toast,
  VerifiedBadge,
  type SwipeCardHandle,
} from "./index.js";
import {
  setAnnouncerVolume,
  setTheme,
  uiStore,
  useStore,
  type AnnouncerVolume,
  type ThemeChoice,
} from "../lib/store.js";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 36 }}>
      <h2
        className="h-caps"
        style={{ fontSize: "0.8rem", color: "var(--text-faint)", marginBottom: 12 }}
      >
        {title}
      </h2>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
        {children}
      </div>
    </section>
  );
}

const TOKEN_SWATCHES = [
  "--bg",
  "--bg-sunken",
  "--surface-1",
  "--surface-2",
  "--surface-3",
  "--line",
  "--text",
  "--text-dim",
  "--accent",
  "--like",
  "--reject",
  "--sr",
  "--gold",
  "--berserk",
  "--shield",
  "--danger",
] as const;

const GRADIENTS = ["--grad-accent", "--grad-like", "--grad-sr", "--grad-gold", "--grad-berserk"] as const;

export function DesignGallery() {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [seriousSheetOpen, setSeriousSheetOpen] = useState(false);
  const [reveal, setReveal] = useState<"classic" | "reject" | null>(null);
  const [lastVerdict, setLastVerdict] = useState<string>("—");
  const [quota, setQuota] = useState(12);
  const [pageKey, setPageKey] = useState(0);
  const swipeRef = useRef<SwipeCardHandle>(null);
  const theme = useStore(uiStore, (s) => s.theme);
  const volume = useStore(uiStore, (s) => s.announcerVolume);

  return (
    <div className="frame__main" style={{ paddingTop: 24, maxWidth: 760 }}>
      <AmbientBackground />
      <GrainOverlay />
      <header style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
        <IconBrokenHeart width={32} height={32} className="brand-mark" />
        <div>
          <h1 className="h-display" style={{ fontSize: "1.8rem" }}>
            Humble design system <span className="grad-text">v5</span>
          </h1>
          <p className="text-dim" style={{ margin: 0, fontSize: "0.9rem" }}>
            /_design — QA gallery. Every primitive, both themes, all motifs, all motion.
          </p>
        </div>
      </header>

      <Section title="Theme / announcer volume (PRD §10)">
        {(["system", "light", "dark"] as ThemeChoice[]).map((t) => (
          <Button key={t} size="sm" variant={theme === t ? "primary" : "default"} onClick={() => setTheme(t)}>
            {t}
          </Button>
        ))}
        <span className="text-faint">·</span>
        {(["off", "text", "full"] as AnnouncerVolume[]).map((v) => (
          <Button
            key={v}
            size="sm"
            variant={volume === v ? "primary" : "default"}
            onClick={() => setAnnouncerVolume(v)}
          >
            announcer: {v}
          </Button>
        ))}
      </Section>

      <Section title="Type contrast — Anton shouts · Instrument Serif smirks · Space Grotesk behaves">
        <div style={{ width: "100%" }}>
          <p className="type-display-2" style={{ margin: 0 }}>
            Being <span className="grad-text">hated</span>{" "}
            <em className="type-editorial" style={{ textTransform: "none", fontSize: "0.92em" }}>
              is a lead.
            </em>
          </p>
          <p className="type-display-3 neon-text" style={{ color: "var(--gold)", margin: "10px 0 0" }}>
            Godlike · Announcer caps
          </p>
          <p className="type-editorial text-dim" style={{ margin: "10px 0 0", fontSize: "1.15rem" }}>
            The editorial register — for the tender, deadpan one-liners.
          </p>
          <p className="text-dim" style={{ margin: "8px 0 0" }}>
            Body/UI stays on Space Grotesk — readable, quiet, lets the display face shout.
          </p>
          <p className="type-numeric" style={{ margin: "8px 0 0", fontSize: "1.3rem", fontWeight: 800 }}>
            07:59:43 · 12/50 · ×10
            <span className="text-faint" style={{ fontSize: "0.8rem", fontWeight: 400 }}>
              {" "}
              (tabular — timers never jitter)
            </span>
          </p>
        </div>
      </Section>

      <Section title="Texture — grain is app-wide; CRT is game-layer only; glass is wet">
        <Card className="crt crt--glow" style={{ width: 230 }}>
          <p className="type-display-3" style={{ color: "var(--sr)", margin: 0 }}>
            CRT panel
          </p>
          <p className="text-dim" style={{ margin: "6px 0 0", fontSize: "0.9rem" }}>
            Scanlines + phosphor breathing. Super-reject, berserker, roulette.
          </p>
        </Card>
        <div className="glass" style={{ width: 230, borderRadius: "var(--radius-lg)", padding: 18 }}>
          <strong>Wet glass</strong>
          <p className="text-dim" style={{ margin: "6px 0 0", fontSize: "0.9rem" }}>
            Backdrop blur + 1px inner rim light.
          </p>
        </div>
        <Button size="sm" onClick={() => shakeScreen("soft")}>
          shake: soft
        </Button>
        <Button size="sm" onClick={() => shakeScreen("hard")}>
          shake: hard
        </Button>
      </Section>

      <Section title="Mascot — the cracked heart, doing something (never a bare spinner)">
        <Mascot mood="idle" />
        <Mascot mood="loading" quip="Consulting the judges…" />
        <Mascot mood="ko" quip="Nothing here. Statistically temporary." />
      </Section>

      <Section title="Tokens — layered surfaces + verbs">
        {TOKEN_SWATCHES.map((v) => (
          <div key={v} style={{ textAlign: "center", fontSize: "0.68rem" }}>
            <div
              style={{
                width: 56,
                height: 36,
                borderRadius: 8,
                border: "1px solid var(--line)",
                background: `var(${v})`,
              }}
            />
            <code>{v}</code>
          </div>
        ))}
      </Section>

      <Section title="Gradients + glows (game layer)">
        {GRADIENTS.map((g) => (
          <div key={g} style={{ textAlign: "center", fontSize: "0.68rem" }}>
            <div
              style={{
                width: 92,
                height: 36,
                borderRadius: 10,
                background: `var(${g})`,
              }}
            />
            <code>{g}</code>
          </div>
        ))}
      </Section>

      <Section title="Buttons — press = spring scale; game variants glow on hover">
        <Button variant="primary">Enter the pit</Button>
        <Button>Default</Button>
        <Button variant="ghost">Ghost</Button>
        <Button variant="danger">Block</Button>
        <Button variant="like">Like</Button>
        <Button variant="reject">Reject</Button>
        <Button variant="sr">
          <IconSkull width={16} height={16} /> Super Reject
        </Button>
        <Button variant="gold">Killstreak</Button>
        <Button variant="berserk">Berserker</Button>
        <Button variant="primary" loading>
          Loading
        </Button>
        <Button disabled>Disabled</Button>
        <Button size="sm">Small</Button>
        <Button size="lg">Large</Button>
      </Section>

      <Section title="Cards — depth + glow motifs; `interactive` lifts on hover">
        <Card interactive style={{ width: 200 }}>
          <strong>Plain card</strong>
          <p className="text-dim" style={{ margin: "6px 0 0", fontSize: "0.9rem" }}>
            The default surface. Hover me.
          </p>
        </Card>
        <Card glow="gold" style={{ width: 200 }}>
          <strong>Killstreak glow</strong>
          <p className="text-dim" style={{ margin: "6px 0 0", fontSize: "0.9rem" }}>
            Tier cosmetics.
          </p>
        </Card>
        <Card glow="berserk" style={{ width: 200 }}>
          <strong>Berserker glow</strong>
          <p className="text-dim" style={{ margin: "6px 0 0", fontSize: "0.9rem" }}>
            Theater, honestly labeled.
          </p>
        </Card>
        <Card glow="sr" style={{ width: 200 }}>
          <strong>Super-reject glow</strong>
          <p className="text-dim" style={{ margin: "6px 0 0", fontSize: "0.9rem" }}>
            The violet one.
          </p>
        </Card>
        <Card glow="accent" style={{ width: 200 }}>
          <strong>Accent glow</strong>
          <p className="text-dim" style={{ margin: "6px 0 0", fontSize: "0.9rem" }}>
            Reject-Match moments.
          </p>
        </Card>
        <Card glow="like" style={{ width: 200 }}>
          <strong>Shield glow</strong>
          <p className="text-dim" style={{ margin: "6px 0 0", fontSize: "0.9rem" }}>
            <IconShield width={14} height={14} style={{ verticalAlign: "-2px" }} /> Cyan cover.
          </p>
        </Card>
      </Section>

      <Section title="Badges">
        <VerifiedBadge />
        <KillstreakBadge tier={3} count={3} />
        <KillstreakBadge tier={5} count={6} />
        <KillstreakBadge tier={10} count={11} />
        <BerserkerBadge />
        <HijackedBadge name="Sam" />
      </Section>

      <Section title="Quota chips — count pops + meter glides on change (S-10 HUD)">
        <QuotaChip label="Likes" used={quota} cap={50} />
        <QuotaChip label="Rejects" used={46} cap={50} />
        <QuotaChip label="Super Rejects" used={5} cap={5} />
        <Button size="sm" onClick={() => setQuota((q) => Math.min(50, q + 1))}>
          spend one
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setQuota(12)}>
          reset
        </Button>
      </Section>

      <Section title="Skeleton (loading rule: shimmer, never spinners-on-white)">
        <div style={{ width: 260, display: "flex", flexDirection: "column", gap: 8 }}>
          <Skeleton height={120} radius={14} />
          <Skeleton width="70%" />
          <Skeleton width="45%" />
        </div>
      </Section>

      <Section title="Toasts — slide-in spring, tap to dismiss">
        <Button size="sm" onClick={() => toast(resolveCopy("deck.rewind_toast") ?? "")}>
          info
        </Button>
        <Button size="sm" onClick={() => toast(resolveCopy("chat.rate_limited") ?? "", "error")}>
          error
        </Button>
        <Button size="sm" onClick={() => toast(resolveCopy("store.success") ?? "", "success")}>
          success
        </Button>
      </Section>

      <Section title="Announcer — arcade slide + overshoot; motif glows (S-22, respects volume + reduced-motion)">
        <Button
          size="sm"
          onClick={() => announce({ sting: resolveCopy("announcer.tiers.3") ?? "", motif: "gold" })}
        >
          First Blood
        </Button>
        <Button
          size="sm"
          onClick={() =>
            announce({ sting: resolveCopy("announcer.tiers.10") ?? "", motif: "gold", combo: 5 })
          }
        >
          GODLIKE (combo ×5)
        </Button>
        <Button
          size="sm"
          onClick={() =>
            announce({ sting: resolveCopy("announcer.berserk_start") ?? "", motif: "berserk" })
          }
        >
          Berserker
        </Button>
        <Button
          size="sm"
          onClick={() =>
            announce({ sting: "SUPER-REJECTED.", sub: "delivered personally", motif: "sr" })
          }
        >
          Super-reject motif
        </Button>
        <Button
          size="sm"
          onClick={() =>
            announce({ sting: resolveCopy("match.reject_title") ?? "", motif: "accent" })
          }
        >
          Accent motif
        </Button>
      </Section>

      <Section title="Sheets — spring-up bottom sheet / centered dialog ≥700px">
        <Button size="sm" onClick={() => setSheetOpen(true)}>
          Open sheet
        </Button>
        <Button size="sm" variant="danger" onClick={() => setSeriousSheetOpen(true)}>
          Serious sheet (S-35 style)
        </Button>
      </Section>

      <Section title="Reveal — the match-reveal takeover (S-12). Reduced motion → plain fade">
        <Button size="sm" variant="primary" onClick={() => setReveal("classic")}>
          Classic match
        </Button>
        <Button size="sm" variant="reject" onClick={() => setReveal("reject")}>
          REJECT-MATCH
        </Button>
      </Section>

      <Section title="SwipeCard — drag me: spring physics, wash + stamp scale with distance; flick to commit">
        <div style={{ width: 280 }}>
          <SwipeCard ref={swipeRef} onVerdict={(v) => setLastVerdict(v)}>
            <div
              style={{
                height: 360,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                padding: 18,
                background: "linear-gradient(180deg, var(--surface-3) 0%, var(--surface-1) 70%)",
              }}
            >
              <div style={{ fontSize: "3rem" }} aria-hidden="true">
                🃏
              </div>
              <strong style={{ fontSize: "1.2rem", fontFamily: "var(--font-display)" }}>
                Placeholder, 99
              </strong>
              <span className="text-dim" style={{ fontSize: "0.9rem" }}>
                2 km away · drag me either way
              </span>
            </div>
          </SwipeCard>
          <div
            style={{
              display: "flex",
              gap: 8,
              marginTop: 12,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Button size="sm" variant="reject" onClick={() => swipeRef.current?.swipe("reject")}>
              {copy.deck.swipe_labels.left}
            </Button>
            <Button size="sm" variant="sr" onClick={() => swipeRef.current?.swipe("super_reject")}>
              <IconSkull width={14} height={14} />
            </Button>
            <Button size="sm" variant="like" onClick={() => swipeRef.current?.swipe("like")}>
              {copy.deck.swipe_labels.right}
            </Button>
            <span className="text-dim" style={{ alignSelf: "center", fontSize: "0.85rem" }}>
              last: {lastVerdict}
            </span>
          </div>
        </div>
      </Section>

      <Section title="RouteTransition — page swaps fade + rise (wraps every screen in the shell)">
        <motion.div
          key={pageKey}
          variants={pageVariants}
          initial="initial"
          animate="enter"
          style={{ width: 240 }}
        >
          <Card>
            <strong>Screen {pageKey + 1}</strong>
            <p className="text-dim" style={{ margin: "6px 0 0", fontSize: "0.9rem" }}>
              This card enters exactly like a route does.
            </p>
          </Card>
        </motion.div>
        <Button size="sm" onClick={() => setPageKey((k) => k + 1)}>
          replay
        </Button>
      </Section>

      <Sheet open={sheetOpen} onClose={() => setSheetOpen(false)} title="A regular sheet">
        <p className="text-dim">
          Bottom sheet on mobile, centered dialog on desktop. Escape and backdrop close it.
        </p>
        <Button block variant="primary" onClick={() => setSheetOpen(false)}>
          Done
        </Button>
      </Sheet>
      <Sheet
        open={seriousSheetOpen}
        onClose={() => setSeriousSheetOpen(false)}
        title="Block Sam?"
        dismissable={false}
      >
        {/* Safety surface: dead serious, no humor (copy-tone §1.4). */}
        <p>{resolveCopy("safety.block_confirm")?.replace("{name}", "Sam")}</p>
        <div style={{ display: "flex", gap: 10 }}>
          <Button block variant="danger" onClick={() => setSeriousSheetOpen(false)}>
            Block
          </Button>
          <Button block onClick={() => setSeriousSheetOpen(false)}>
            Cancel
          </Button>
        </div>
      </Sheet>

      <Reveal
        open={reveal !== null}
        kind={reveal ?? "classic"}
        avatars={["/icons/icon-180.png", "/icons/icon-180.png"]}
        kicker={reveal === "reject" ? "mutual destruction" : "it's mutual"}
        title={
          reveal === "reject"
            ? (resolveCopy("match.reject_title") ?? "REJECT-MATCH.")
            : (resolveCopy("match.classic_title") ?? "A MATCH.")
        }
        onClose={() => setReveal(null)}
      >
        <p className="text-dim" style={{ margin: 0 }}>
          {resolveCopy(reveal === "reject" ? "match.reject_body" : "match.classic_body")}
        </p>
        <div className="rvfx__actions">
          <Button block variant="primary" onClick={() => setReveal(null)}>
            Say something
          </Button>
          <Button block variant="ghost" onClick={() => setReveal(null)}>
            Keep swiping
          </Button>
        </div>
      </Reveal>
    </div>
  );
}
